import { useCallback, useContext, useEffect } from 'react'

import { useAuth0 } from '@/auth/index'
import { Context } from '@/shared/store/reducers/global-reducer'

import useMaxSessionStore from '../stores/use-max-session-store'

const NO_ACTIVE_TENANT_MESSAGE = 'No active Prodigy tenant selected. Chat is blocked.'

type UseMaxChatSessionBootstrapOptions = {
  tenantsLoading: boolean
  enabled?: boolean
}

export const useMaxChatSessionBootstrap = ({
  tenantsLoading,
  enabled = true,
}: UseMaxChatSessionBootstrapOptions) => {
  const { isAuthenticated, getIdTokenClaims, loading: auth0Loading } = useAuth0()
  const { store } = useContext(Context)
  const activeProdigyTenantId = store?.activeTenant?.id

  const initializeSession = useMaxSessionStore((state) => state.initializeSession)
  const setLoading = useMaxSessionStore((state) => state.setLoading)
  const setBlocked = useMaxSessionStore((state) => state.setBlocked)
  const clearSession = useMaxSessionStore((state) => state.clearSession)

  useEffect(() => {
    if (!enabled) return

    if (auth0Loading || tenantsLoading) {
      setLoading({ preserveReady: true })
      return
    }

    if (!isAuthenticated) {
      clearSession()
      return
    }

    if (!activeProdigyTenantId) {
      setBlocked(NO_ACTIVE_TENANT_MESSAGE)
      return
    }

    void initializeSession({
      activeProdigyTenantId,
      getIdTokenClaims,
    })
  }, [
    activeProdigyTenantId,
    auth0Loading,
    clearSession,
    getIdTokenClaims,
    initializeSession,
    isAuthenticated,
    setBlocked,
    setLoading,
    tenantsLoading,
    enabled,
  ])
}

const useMaxChatSession = () => {
  const { isAuthenticated, getIdTokenClaims, loading: auth0Loading } = useAuth0()
  const { store } = useContext(Context)
  const activeProdigyTenantId = store?.activeTenant?.id

  const session = useMaxSessionStore((state) => state.session)
  const initializeSession = useMaxSessionStore((state) => state.initializeSession)
  const setLoading = useMaxSessionStore((state) => state.setLoading)
  const setBlocked = useMaxSessionStore((state) => state.setBlocked)
  const clearSession = useMaxSessionStore((state) => state.clearSession)

  const refreshSession = useCallback(async () => {
    if (auth0Loading) {
      setLoading()
      return
    }

    if (!isAuthenticated) {
      clearSession()
      return
    }

    if (!activeProdigyTenantId) {
      setBlocked(NO_ACTIVE_TENANT_MESSAGE)
      return
    }

    await initializeSession({
      activeProdigyTenantId,
      getIdTokenClaims,
      force: true,
    })
  }, [
    activeProdigyTenantId,
    auth0Loading,
    clearSession,
    getIdTokenClaims,
    initializeSession,
    isAuthenticated,
    setBlocked,
    setLoading,
  ])

  return {
    session,
    refreshSession,
  }
}

export default useMaxChatSession
